import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HistoryAction, LayoutElement } from '../../types';
import { MAX_HISTORY_SIZE } from '../../constants';
import type { AppDispatch, RootState } from '../../store';
import {
  addElements,
  removeElements,
  updateElements,
} from '../layout/layoutSlice';

interface HistoryState {
  past: HistoryAction[];
  future: HistoryAction[];
  currentActionId: string | null;
}

const initialState: HistoryState = {
  past: [],
  future: [],
  currentActionId: null,
};

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {
    addHistoryAction: (state, action: PayloadAction<HistoryAction>) => {
      state.past.push(action.payload);
      state.future = []; // Clear redo stack when new action is added
      state.currentActionId = action.payload.id;

      // Limit history size
      if (state.past.length > MAX_HISTORY_SIZE) {
        state.past.shift();
      }
    },

    performUndo: (state) => {
      const action = state.past.pop();
      if (action) {
        state.future.unshift(action);
        state.currentActionId = state.past[state.past.length - 1]?.id || null;
      }
    },

    performRedo: (state) => {
      const action = state.future.shift();
      if (action) {
        state.past.push(action);
        state.currentActionId = action.id;
      }
    },

    /**
     * Drop the layout redo stack. Dispatched when an *equipment* edit is recorded:
     * undo/redo is global, so any new edit invalidates pending redos everywhere.
     */
    clearFuture: (state) => {
      state.future = [];
    },

    clearHistory: (state) => {
      state.past = [];
      state.future = [];
      state.currentActionId = null;
    },

    setHistoryState: (
      state,
      action: PayloadAction<{ past: HistoryAction[]; future: HistoryAction[] }>
    ) => {
      state.past = action.payload.past;
      state.future = action.payload.future;
      state.currentActionId = state.past[state.past.length - 1]?.id || null;
    },
  },
});

export const {
  addHistoryAction,
  performUndo,
  performRedo,
  clearFuture,
  clearHistory,
  setHistoryState,
} = historySlice.actions;

const toElementUpdates = (elements: HistoryAction['previousState'] = []) =>
  elements
    .filter((element): element is NonNullable<typeof element> => !!element)
    .map((element) => ({
      id: element.id as string,
      updates: element,
    }));

const toLayoutElements = (elements: HistoryAction['previousState'] = []) =>
  elements.filter(
    (element): element is LayoutElement =>
      !!element &&
      typeof element.id === 'string' &&
      typeof element.type === 'string' &&
      !!element.position &&
      !!element.dimensions &&
      !!element.metadata
  );

export const undo = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const action = state.history.past[state.history.past.length - 1];
  if (!action) return;

  if (action.type === 'add') {
    dispatch({
      type: removeElements.type,
      payload: action.elementIds,
      meta: { skipHistory: true },
    });
  } else if (action.type === 'remove') {
    const elements = toLayoutElements(action.previousState);
    if (elements.length > 0) {
      dispatch({
        type: addElements.type,
        payload: elements,
        meta: { skipHistory: true },
      });
    }
  } else if (action.type === 'replace') {
    // Undo a replace: drop the elements it added, restore whatever it removed.
    dispatch({
      type: removeElements.type,
      payload: action.elementIds,
      meta: { skipHistory: true },
    });
    const restored = toLayoutElements(action.previousState);
    if (restored.length > 0) {
      dispatch({
        type: addElements.type,
        payload: restored,
        meta: { skipHistory: true },
      });
    }
  } else {
    const updates = toElementUpdates(action.previousState);
    if (updates.length > 0) {
      dispatch({
        type: updateElements.type,
        payload: updates,
        meta: { skipHistory: true },
      });
    }
  }

  dispatch(performUndo());
};

export const redo = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const action = state.history.future[0];
  if (!action) return;

  if (action.type === 'add') {
    const elements = toLayoutElements(action.newState);
    if (elements.length > 0) {
      dispatch({
        type: addElements.type,
        payload: elements,
        meta: { skipHistory: true },
      });
    }
  } else if (action.type === 'remove') {
    dispatch({
      type: removeElements.type,
      payload: action.elementIds,
      meta: { skipHistory: true },
    });
  } else if (action.type === 'replace') {
    // Redo a replace: remove whatever undo restored, re-add the new elements.
    const restored = toLayoutElements(action.previousState);
    if (restored.length > 0) {
      dispatch({
        type: removeElements.type,
        payload: restored.map((el) => el.id),
        meta: { skipHistory: true },
      });
    }
    const elements = toLayoutElements(action.newState);
    if (elements.length > 0) {
      dispatch({
        type: addElements.type,
        payload: elements,
        meta: { skipHistory: true },
      });
    }
  } else {
    const updates = toElementUpdates(action.newState);
    if (updates.length > 0) {
      dispatch({
        type: updateElements.type,
        payload: updates,
        meta: { skipHistory: true },
      });
    }
  }

  dispatch(performRedo());
};

export default historySlice.reducer;
